/* eslint-disable @typescript-eslint/no-explicit-any */
export const ORDER_COUPON_SUFFIX = '_orderCoupon';

/**
 * Kiểm tra xem một voucher key có phải là voucher áp dụng cho toàn bộ đơn hàng hay không.
 * Định dạng chuẩn: `${couponCode}_orderCoupon`
 */
export function isOrderVoucherKey(key: string): boolean {
  return typeof key === 'string' && key.endsWith(ORDER_COUPON_SUFFIX);
}

/**
 * Kiểm tra tính hợp lệ của một voucher key (không rỗng, không chứa 'undefined').
 */
export function isValidVoucherKey(key?: string | null): key is string {
  return (
    typeof key === 'string' &&
    key.trim().length > 0 &&
    !key.endsWith('_undefined') &&
    !key.includes('undefined')
  );
}

/**
 * Lọc sạch danh sách voucher key, loại bỏ các key rác hoặc lỗi undefined.
 */
export function sanitizeVoucherKeys(keys: string[]): string[] {
  if (!Array.isArray(keys)) return [];
  return keys.filter(isValidVoucherKey);
}

/**
 * Kiểm tra xem một voucher key có phải là voucher áp dụng cho sản phẩm hay không.
 * Nếu truyền productId: kiểm tra xem có đúng là voucher của sản phẩm đó không (`${couponCode}_${productId}`).
 */
export function isProductVoucherKey(key: string, productId?: string): boolean {
  if (typeof key !== 'string' || isOrderVoucherKey(key) || !isValidVoucherKey(key)) return false;
  if (productId) {
    return key.endsWith(`_${productId}`);
  }
  return key.includes('_');
}

/**
 * Trích xuất mã coupon gốc từ key (bỏ phần đuôi `_${targetId}`).
 */
export function getCouponCodeFromKey(key: string): string {
  if (typeof key !== 'string') return '';
  const lastIndex = key.lastIndexOf('_');
  if (lastIndex === -1) return key;
  return key.substring(0, lastIndex);
}

/**
 * Trích xuất productId từ key. Trả về null nếu là voucher đơn hàng hoặc không có productId / undefined.
 */
export function getProductIdFromKey(key: string): string | null {
  if (typeof key !== 'string' || isOrderVoucherKey(key) || !isValidVoucherKey(key)) return null;
  const lastIndex = key.lastIndexOf('_');
  if (lastIndex === -1) return null;
  const id = key.substring(lastIndex + 1);
  if (!id || id === 'undefined') return null;
  return id;
}

export interface ProductInfoMinimal {
  _id: string;
  name?: { vi?: string; en?: string } | string;
  code?: string;
  [key: string]: any;
}

/**
 * Tìm kiếm thông tin sản phẩm trong giỏ hàng theo ID (hỗ trợ cả sản phẩm chính, bundles, quà tặng giftItems và fallback từ cartCoupons).
 */
export function findProductInCart(
  cart: any,
  pId: string,
  cartCoupons?: any[]
): ProductInfoMinimal | null {
  if (!pId || pId === 'undefined') return null;

  // 1. Tìm trong danh sách sản phẩm chính của giỏ hàng
  for (const p of cart?.products || []) {
    if (p?._id === pId || p?.productId === pId) {
      return p;
    }
    // 2. Tìm trong danh sách sản phẩm bundle mua kèm (License Key, phụ kiện,...)
    if (Array.isArray(p?.bundles)) {
      const bundle = p.bundles.find(
        (b: any) => b?._id === pId || b?.productId === pId
      );
      if (bundle) return bundle;
    }
    // 3. Tìm trong danh sách quà tặng giftItems
    if (Array.isArray(p?.giftItems)) {
      const gift = p.giftItems.find(
        (g: any) => g?._id === pId || g?.giftId === pId || g?.targetId === pId
      );
      if (gift) return gift;
    }
  }

  // 4. Fallback tìm trong danh sách cartCoupons nếu backend đã enrich sẵn thông tin product
  if (Array.isArray(cartCoupons)) {
    const couponWithProd = cartCoupons.find(
      (c: any) => c?.product?._id === pId || c?.product?.productId === pId
    );
    if (couponWithProd?.product) {
      return couponWithProd.product;
    }
  }

  return null;
}

/**
 * Tạo fallback product info khi không tìm thấy product trong cart để đảm bảo không bị undefined
 */
export function enrichProductFallback(
  pId: string,
  couponSample?: any
): ProductInfoMinimal {
  return {
    _id: pId,
    name: couponSample?.name || { vi: couponSample?.code || pId },
    code: couponSample?.code || pId,
  };
}

export interface SmartOverrideParams {
  currentSelected: string[];
  newCouponCode: string;
  target: 'order' | 'product';
  applicableProductIds?: string[];
}

/**
 * Thuật toán Smart Override Engine:
 * - Khi nhập/chọn voucher đơn hàng: ghi đè lên voucher đơn hàng cũ trong danh sách, giữ nguyên voucher sản phẩm.
 * - Khi nhập/chọn voucher sản phẩm: ghi đè lên voucher cũ của các sản phẩm thỏa mãn điều kiện, giữ nguyên voucher đơn hàng và voucher của sản phẩm khác.
 */
export function applyVoucherSmartOverride({
  currentSelected,
  newCouponCode,
  target,
  applicableProductIds = [],
}: SmartOverrideParams): string[] {
  const normalizedCode = (newCouponCode || '').trim().toUpperCase();
  if (!normalizedCode) {
    return sanitizeVoucherKeys(currentSelected);
  }

  let updated = sanitizeVoucherKeys(currentSelected);

  if (target === 'order') {
    // 1. Loại bỏ voucher đơn hàng cũ
    updated = updated.filter(key => !isOrderVoucherKey(key));
    // 2. Thêm voucher đơn hàng mới
    updated.push(`${normalizedCode}${ORDER_COUPON_SUFFIX}`);
  } else if (target === 'product') {
    // Với mỗi sản phẩm thỏa mãn điều kiện (bỏ qua undefined hoặc rỗng)
    applicableProductIds.forEach(productId => {
      if (!productId || productId === 'undefined') return;
      // 1. Loại bỏ voucher cũ của chính sản phẩm này
      updated = updated.filter(key => !isProductVoucherKey(key, productId));
      // 2. Thêm voucher mới cho sản phẩm này
      updated.push(`${normalizedCode}_${productId}`);
    });
  }

  return Array.from(new Set(sanitizeVoucherKeys(updated)));
}

/**
 * Xử lý sự kiện click chọn/bỏ chọn thẻ voucher trên giao diện danh sách:
 * - Nếu là uncheck: loại bỏ thẻ đó khỏi danh sách.
 * - Nếu là check: áp dụng quy tắc tối đa 1 voucher đơn hàng và tối đa 1 voucher cho mỗi sản phẩm.
 */
export function resolveSelectionChange(
  previousSelected: string[],
  nextSelected: string[]
): string[] {
  const cleanNext = sanitizeVoucherKeys(nextSelected);
  const cleanPrev = sanitizeVoucherKeys(previousSelected);

  // Tìm item vừa được thêm vào (nếu có)
  const addedItem = cleanNext.find(item => !cleanPrev.includes(item));
  // Tìm item vừa bị gỡ bỏ (nếu có)
  const removedItem = cleanPrev.find(item => !cleanNext.includes(item));

  // Nếu là thao tác uncheck (gỡ bỏ)
  if (removedItem && !addedItem) {
    return cleanNext;
  }

  // Nếu là thao tác check (thêm vào)
  if (addedItem) {
    const isOrder = isOrderVoucherKey(addedItem);
    const addedProductId = getProductIdFromKey(addedItem);

    return cleanNext.filter(item => {
      // Giữ lại item mới vừa chọn
      if (item === addedItem) return true;

      // Nếu item vừa chọn là voucher đơn hàng -> loại bỏ các voucher đơn hàng khác
      if (isOrder && isOrderVoucherKey(item)) {
        return false;
      }

      // Nếu item vừa chọn là voucher của sản phẩm X -> loại bỏ các voucher khác của chính sản phẩm X
      if (addedProductId && isProductVoucherKey(item, addedProductId)) {
        return false;
      }

      return true;
    });
  }

  return cleanNext;
}

/**
 * Chuẩn hóa một voucher item:
 * - Đảm bảo code luôn tồn tại (fallback từ couponCode hoặc trích xuất từ initialKey)
 * - Tự động uppercase và trim khoảng trắng
 * - Bổ sung thông tin product nếu thiếu
 */
export function normalizeVoucherItem<T = any>(
  rawCoupon: any,
  targetProduct?: any
): T {
  if (!rawCoupon || typeof rawCoupon !== 'object') return rawCoupon;

  const rawCode =
    rawCoupon.code ||
    rawCoupon.couponCode ||
    getCouponCodeFromKey(rawCoupon.initialKey) ||
    '';
  const normalizedCode = (typeof rawCode === 'string' ? rawCode : '')
    .trim()
    .toUpperCase();

  const isOrder =
    rawCoupon.target === 'order' ||
    rawCoupon.target === 1 ||
    isOrderVoucherKey(rawCoupon.initialKey);

  return {
    ...rawCoupon,
    code: normalizedCode,
    couponCode: normalizedCode,
    target: isOrder ? 'order' : 'product',
    ...(targetProduct ? { product: targetProduct } : {}),
  } as T;
}

/**
 * Trích xuất danh sách các voucher đang được áp dụng thành công trong giỏ hàng (cart.coupons)
 */
export function extractAppliedVouchersFromCart(
  cart: any,
  cartCouponsFromApi: any[] = []
): any[] {
  if (!cart?.coupons || !Array.isArray(cart.coupons)) return [];

  const appliedItems: any[] = [];

  for (const item of cart.coupons) {
    if (item?.mes || !item?.coupon) continue;

    const rawCoupon = item.coupon;
    const initialKey = rawCoupon.initialKey || '';
    const isOrder =
      rawCoupon.target === 'order' ||
      rawCoupon.target === 1 ||
      isOrderVoucherKey(initialKey);

    const targetId = isOrder
      ? 'orderCoupon'
      : rawCoupon.targetId ||
        getProductIdFromKey(initialKey) ||
        rawCoupon.product?._id;

    if (!isOrder && (!targetId || targetId === 'undefined')) continue;

    const prod = isOrder
      ? undefined
      : rawCoupon.product ||
        findProductInCart(cart, targetId, cartCouponsFromApi) ||
        enrichProductFallback(targetId, rawCoupon);

    appliedItems.push(normalizeVoucherItem(rawCoupon, prod));
  }

  return appliedItems;
}

export interface ReconcileManualVouchersParams {
  cart: any;
  cartCouponsFromApi?: any[];
  sessionCommittedManual?: any[];
}

/**
 * Tái tạo và đồng bộ danh sách manual / private vouchers:
 * - Khi reload (F5) trang, sessionCommittedManual có thể rỗng [].
 * - Hàm này sẽ trích xuất các applied vouchers đang có hiệu lực trong cart.coupons mà không nằm trong danh sách public vouchers (cartCouponsFromApi).
 * - Kết hợp an toàn với sessionCommittedManual (chỉ giữ các voucher còn đang áp dụng trong cart).
 * - Loại bỏ trùng lặp dựa trên `${code}_${targetId}`.
 */
export function reconcileManualVouchers({
  cart,
  cartCouponsFromApi = [],
  sessionCommittedManual = [],
}: ReconcileManualVouchersParams): any[] {
  const appliedVouchers = extractAppliedVouchersFromCart(
    cart,
    cartCouponsFromApi
  );

  // Tập hợp mã các public voucher từ API getCartCoupons
  const publicCodes = new Set(
    (cartCouponsFromApi || [])
      .map(c =>
        (c?.code || c?.couponCode || getCouponCodeFromKey(c?.initialKey) || '')
          .trim()
          .toUpperCase()
      )
      .filter(Boolean)
  );

  // Lọc các voucher đang áp dụng trong giỏ hàng mà KHÔNG PHẢI là public coupon (tức là private / manual voucher)
  const appliedManualVouchers = appliedVouchers.filter(v => {
    const code = (v?.code || '').trim().toUpperCase();
    return code && !publicCodes.has(code);
  });

  // Gộp cả applied từ giỏ hàng và committed từ session hiện tại
  const candidatePool = [
    ...(sessionCommittedManual || []),
    ...appliedManualVouchers,
  ];

  // Tập hợp các mã voucher đang thực sự áp dụng và hợp lệ trong giỏ hàng
  const appliedCodesInCart = new Set(
    appliedVouchers.map(v => (v?.code || '').trim().toUpperCase())
  );

  // Chỉ giữ các candidate có code đang áp dụng trong giỏ hàng
  const validCandidates = candidatePool.filter(c => {
    const code = (
      c?.code ||
      c?.couponCode ||
      getCouponCodeFromKey(c?.initialKey) ||
      ''
    )
      .trim()
      .toUpperCase();
    return code && appliedCodesInCart.has(code);
  });

  // Loại bỏ trùng lặp dựa trên combo `${code}_${targetId}`
  const uniqueMap = new Map<string, any>();
  for (const item of validCandidates) {
    const normalized = normalizeVoucherItem(item);
    const targetId =
      normalized.target === 'order'
        ? 'orderCoupon'
        : normalized.product?._id ||
          normalized.targetId ||
          getProductIdFromKey(normalized.initialKey) ||
          '';
    const key = `${normalized.code}_${targetId}`;
    if (!uniqueMap.has(key)) {
      uniqueMap.set(key, normalized);
    }
  }

  return Array.from(uniqueMap.values());
}
